import React, { Suspense } from "react";
import AudioPlayer from "./FileDisplay/AudioPlayer";
import "react-h5-audio-player/lib/styles.css";
import TxtDisplay from "./FileDisplay/TxtDisplay";
import { VIEWER_HEIGHT, ViewerPlaceholder } from "./FileDisplay/ViewerChrome";
import ViewerErrorBoundary from "./FileDisplay/ViewerErrorBoundary";
import { API_ENDPOINT } from "../Settings";
import VideoJS from "./FileDisplay/VideojsPlayer";

/**
 * Split out of the main bundle, unlike the media players here.
 *
 * The two document viewers are by far the most expensive things this component
 * can reach: pdf.js and its worker on one side, the docx editor with its core,
 * agent and i18n packages plus the generated font-substitution stylesheets on
 * the other. Imported eagerly, every visitor pays for all of it to look at a
 * JPEG — including on the unauthenticated sharing-link route, which is often
 * someone's only visit.
 *
 * The docx stylesheets are the part that makes this more than a size question.
 * They declare `@font-face` under real Microsoft family names, which is
 * document-global and cannot be scoped (see DocxViewer.tsx), so a static
 * import puts them in front of readers who never open a .docx at all.
 *
 * The chrome those viewers share is imported from ViewerChrome rather than
 * DocumentViewerFrame for the same reason — the frame reaches the zoom toolbar,
 * which belongs in the chunk with the viewer it controls.
 */
const PdfViewer = React.lazy(() => import("./FileDisplay/PdfViewer"));
const DocxViewer = React.lazy(() => import("./FileDisplay/DocxViewer"));

/**
 * A lazily-loaded document viewer, with the states a chunk-loaded viewer has
 * before it is a viewer.
 *
 * key={url}: a different document is a different component. Both viewers render
 * into a DOM node the rendering library owns and neither library's render can be
 * aborted, so a remount — rather than a reset — is what stops a slow render for
 * the previous version repainting over the current one. Keying the boundary does
 * that for the viewer inside it and gives the new document a boundary that has
 * not already failed.
 *
 * The chunk's own loading state is the empty frame, so there is no size jump
 * when the viewer arrives and draws its spinner in place — and a chunk that
 * never arrives is a failure the boundary shows in that same frame, rather than
 * one that takes the page down with it.
 */
const documentViewer = (Viewer: React.ComponentType<{ url: string }>, url: string) => (
  <ViewerErrorBoundary key={url}>
    <Suspense fallback={<ViewerPlaceholder />}>
      <Viewer url={url} />
    </Suspense>
  </ViewerErrorBoundary>
);

const extentionsToMimeType = {
  ".aac": "audio/x-aac",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".flac": "audio/flac",
  ".jpeg": "image/jpeg",
  ".jpg": "image/jpeg",
  ".jpgm": "video/jpm",
  ".jpgv": "video/jpeg",
  ".m4a": "audio/x-m4a",
  ".mov": "video/quicktime",
  ".mp3": "audio/mpeg",
  ".mp4": "video/mp4",
  ".mp4a": "audio/mp4",
  ".mp4v": "video/mp4",
  ".mpa": "video/mpeg",
  ".mpe": "video/mpeg",
  ".mpeg": "video/mpeg",
  ".mpg": "video/mpeg",
  ".mpg4": "video/mp4",
  ".mpga": "audio/mpeg",
  ".oga": "audio/ogg",
  ".ogg": "audio/ogg",
  ".ogv": "video/ogg",
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".wav": "audio/x-wav",
};

interface FileDisplayProps {
  url: string;
  fileExtention: string;
  mimeType?: string;
  style?: React.CSSProperties;
  fileName?: string;
  createdBy?: string;
  getUpdatedFileDownloadUrl?: () => any
}

const FileDisplay: React.FC<FileDisplayProps> = ({
  url,
  fileExtention,
  mimeType,
  style,
  fileName,
  createdBy,
  getUpdatedFileDownloadUrl
}) => {
  if (url.startsWith("/")) {
    url = API_ENDPOINT + url;
    mimeType = new URL(url).searchParams.get("content_type") || "";
  }
  // File extension is only set after file has been processed in background. We can use extension from url to get the file extension if it is not set.
  if (!fileExtention) {
    // get extension from url, ignore query params
    fileExtention = url.split("?")[0].split(".").pop() || "";
  }
  if (!mimeType) {
    mimeType = extentionsToMimeType["." + fileExtention];
  }
  switch (mimeType) {
    case "application/pdf":
      return documentViewer(PdfViewer, url);

    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return documentViewer(DocxViewer, url);

    case "audio/mpeg":
    case "audio/wav":
    case "audio/x-wav":
    case "audio/wave":
    case "audio/vnd.wav":
    case "audio/vnd.wave":
    case "audio/mp3":
    case "audio/ogg":
    case "audio/flac":
    case "audio/x-flac":
    case "audio/x-aac":
    case "audio/x-m4a":
    case "audio/mp4":
    case "audio/mp4a":
    case "audio/aac":
    case "video/3gpp": // Libmagic returns video/3gpp for this m4a file
      return (
        <AudioPlayer src={url} fileName={fileName} createdBy={createdBy} getUpdatedFileDownloadUrl={getUpdatedFileDownloadUrl} />
      );
    case "text/plain":
      return <TxtDisplay url={url} />
    case "video/mp4":
    case "video/quicktime":
      return (
        <div style={{
          background: "white",
          width: "100%",
          // height: "80vh",
        }}>
          <VideoJS src={url} getUpdatedFileDownloadUrl={getUpdatedFileDownloadUrl} contentType={mimeType} />
        </div>
      );
    default:
      return (
        <embed
          type={mimeType}
          src={url}
          style={{
            ...style,
            background: "white",
            height: VIEWER_HEIGHT,
            width: "100%",
          }}
        />
      );
  }
};

export default FileDisplay;
